import type { ResourceTypeHandle } from '@toolproof-core/genesis/_contract/_index';

import { CONSTANTS } from '@toolproof-core/lib/_data/lookups';

import { resolveRingItemAngle } from '@toolproof-core/visualization/_pure/ring-layout';
import type { ItemCollection } from '@toolproof-core/visualization/_contract/collection';
import type {
    EntityItem,
} from '@toolproof-core/visualization/_contract/item';
import type { ItemKey } from '@toolproof-core/visualization/_contract/key';
import type { RingAngularPlacement } from '@toolproof-core/visualization/_contract/layout';

import type { BaseConfig } from '@/workflow/_contract/config';
import type { ResourceTypeAngularInfo } from '@/workflow/_contract/kernel';

export function deriveResourceTypeAngularPlacement(args: {
    resourceTypeItems: readonly EntityItem[];
    config: BaseConfig;
}): RingAngularPlacement {
    const placement = args.config.domainPresentation.entities.resourceTypes.angularPlacement;
    if (placement.placementKind === 'evenly-spaced') {
        const startAngle = placement.startAngle ?? 0;
        if (!Number.isFinite(startAngle)) {
            throw new Error('ResourceType ring startAngle must be finite.');
        }
        return { placementKind: 'evenly-spaced', startAngle };
    }

    const angleByItemKey: Partial<Record<ItemKey<'entity'>, number>> = {};
    const itemKeyByNormalizedAngle = new Map<number, ItemKey<'entity'>>();
    const itemKeyPrefix = 'item:entity:';
    for (const item of args.resourceTypeItems) {
        if (!item.itemKey.startsWith(itemKeyPrefix)) {
            throw new Error(`Cannot derive a ResourceType handle from '${item.itemKey}'.`);
        }
        const resourceTypeHandle = item.itemKey.slice(itemKeyPrefix.length) as ResourceTypeHandle;
        const angle = placement.angleByResourceTypeHandle[resourceTypeHandle];
        if (angle === undefined) {
            throw new Error(`Explicit ResourceType angular placement has no angle for '${resourceTypeHandle}'.`);
        }
        if (!Number.isFinite(angle)) {
            throw new Error(`Explicit ResourceType angle for '${item.itemKey}' must be finite.`);
        }

        const normalizedAngle = ((angle % (Math.PI * 2)) + Math.PI * 2) % (Math.PI * 2);
        const existingItemKey = itemKeyByNormalizedAngle.get(normalizedAngle);
        if (existingItemKey !== undefined) {
            throw new Error(`Explicit ResourceType angles overlap for '${existingItemKey}' and '${item.itemKey}'.`);
        }
        itemKeyByNormalizedAngle.set(normalizedAngle, item.itemKey);
        angleByItemKey[item.itemKey] = angle;
    }

    return { placementKind: 'explicit', angleByItemKey };
}

export function deriveResourceTypeAngularInfo(args: {
    itemCollections: ItemCollection[];
    config: BaseConfig;
}): ResourceTypeAngularInfo {
    const resourceTypeCollection = args.itemCollections.find(
        (itemCollection): itemCollection is ItemCollection<'entity'> =>
            itemCollection.itemCollectionKind === 'entity'
            && itemCollection.itemCollectionKey
                === (`collection:entity:${CONSTANTS.Handles.ResourceTypes.TYPE_ResourceType}` as ItemCollection<'entity'>['itemCollectionKey']),
    );

    const angleByHandle = new Map<ResourceTypeHandle, number>();
    if (!resourceTypeCollection || resourceTypeCollection.items.length === 0) {
        return { angleByHandle, resourceTypeCount: 0 };
    }

    const angularPlacement = deriveResourceTypeAngularPlacement({
        resourceTypeItems: resourceTypeCollection.items,
        config: args.config,
    });
    resourceTypeCollection.items.forEach((item, index) => {
        const angle = resolveRingItemAngle({
            layout: {
                layoutKind: 'ring',
                anchor: { anchorKind: 'origin' },
                radius: 0,
                angularPlacement,
            },
            itemKey: item.itemKey,
            itemIndex: index,
            itemCount: resourceTypeCollection.items.length,
        });
        const prefix = 'item:entity:';
        if (!item.itemKey.startsWith(prefix)) return;
        angleByHandle.set(item.itemKey.slice(prefix.length) as ResourceTypeHandle, angle);
    });

    return {
        angleByHandle,
        resourceTypeCount: resourceTypeCollection.items.length,
    };
}

export function resolveMacroStepHelperLaneAngles(args: {
    resourceTypeInfo: ResourceTypeAngularInfo;
    config: BaseConfig;
}): { loopAngle: number; branchAngle: number } {
    const offset = args.config.domainPresentation.connectors.macroStep.booleanLaneAngularOffset;
    if (!Number.isFinite(offset) || offset <= 0) {
        throw new Error('MacroStep booleanLaneAngularOffset must be finite and greater than zero.');
    }
    const booleanAngle = args.resourceTypeInfo.angleByHandle.get(CONSTANTS.Handles.ResourceTypes.TYPE_Boolean);
    if (booleanAngle === undefined || !Number.isFinite(booleanAngle)) {
        throw new Error('MacroStep connectors require a resolved TYPE-Boolean radial.');
    }
    const lowerAngle = args.config.domainPresentation.connectors.macroStep.lowerAngle;
    if (lowerAngle !== 'branch' && lowerAngle !== 'loop') {
        throw new Error("MacroStep lowerAngle must be 'branch' or 'loop'.");
    }
    return {
        loopAngle: booleanAngle + (lowerAngle === 'loop' ? -offset : offset),
        branchAngle: booleanAngle + (lowerAngle === 'branch' ? -offset : offset),
    };
}